import { ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Label } from "../../../../../../components/Fonts";
import { ResetIcon } from "../PreDefinedColorSection";

/* A block of the screen: its title over a card (as the Settings screen's blocks) */
const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  /* as tall as "Redefinir": the card under it doesn't move when the link shows up */
  .title {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    height: 1.125rem;
  }

  .plus {
    display: flex;
  }

  .card {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 20px;
    background: var(--color-neutral-dark);
    border-radius: 1rem;
  }
`;

type Props = {
  title: string;
  /** Beside the title (e.g. the PLUS tag). */
  aside?: ReactNode;
  children: ReactNode;
};

/** A named group of the Personalizar screen (Temas prontos, Fundo, Botões, Texto, Rodapé). */
export const Group = ({ title, aside, children }: Props) => {
  const id = useId();
  return (
    <Container aria-labelledby={id}>
      <span className="title">
        <Label id={id}>{title}</Label>
        {aside}
      </span>
      <div className="card">{children}</div>
    </Container>
  );
};

/* "Redefinir": on the right of a group's title, as a link */
const ResetLink = styled.button`
  display: inline-flex;
  height: 1.125rem;
  line-height: 1;
  gap: 0.375rem;
  align-items: center;
  margin-left: auto;
  color: var(--color-neutral-150);
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  background: none;
  transition: color 0.15s;

  &:hover {
    color: var(--color-neutral-white);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

type ResetProps = {
  /** What it brings back, for screen readers ("Redefinir fontes"). */
  label: string;
  onReset: () => void;
};

/** "Redefinir" beside a group's title (Temas prontos, Fontes): brings back what the screen opened with. */
export const GroupReset = ({ label, onReset }: ResetProps) => {
  const { t } = useTranslation();
  return (
    <ResetLink type="button" aria-label={label} onClick={onReset}>
      <ResetIcon />
      {t("pages.editor.settings.form.predefinedColors.reset")}
    </ResetLink>
  );
};
